/**
 * Minimal, dependency-free .xlsx writer used ONLY for the employee-import
 * template download (header row only). The backend parses .xlsx with ExcelJS and
 * maps headers through its HEADER_MAP, so the header names below are exactly
 * ones that map covers (Arabic names, lower-cased lookup). The zip is "stored"
 * (no compression) which every reader accepts. Pure CommonJS so node:test can
 * exercise it.
 */

// Header text -> backend field (documentation only; the backend does the mapping).
var TEMPLATE_HEADERS = [
  'الاسم',
  'الرقم القومي',
  'رقم الكارت',
  'الموبايل',
  'email',
  'القسم',
  'رقم البوليصة',
  // S10/M12: the employee's TIER (الفئة) — backend resolves it to a contract-tree
  // entity (HEADER_MAP maps 'الفئة' → tier); an unknown tier is a warning.
  'الفئة',
];

var CRC_TABLE = (function () {
  var t = [];
  for (var n = 0; n < 256; n++) {
    var c = n;
    for (var k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes) {
  var c = 0xffffffff;
  for (var i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function utf8(str) {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(str);
  return Uint8Array.from(Buffer.from(str, 'utf8'));
}

function escapeXml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function colLetter(i) {
  return String.fromCharCode(65 + i); // headers are < 26 columns
}

function u16(n) {
  return [n & 0xff, (n >>> 8) & 0xff];
}
function u32(n) {
  return [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff];
}

/** files: [{ name, data: Uint8Array }] -> Uint8Array (zip, stored). */
function zipStore(files) {
  var out = [];
  var central = [];
  var offset = 0;
  files.forEach(function (f) {
    var name = utf8(f.name);
    var crc = crc32(f.data);
    var local = [].concat(
      u32(0x04034b50), u16(20), u16(0x0800), u16(0), u16(0), u16(0x21),
      u32(crc), u32(f.data.length), u32(f.data.length), u16(name.length), u16(0),
    );
    out.push(Uint8Array.from(local), name, f.data);
    var cen = [].concat(
      u32(0x02014b50), u16(20), u16(20), u16(0x0800), u16(0), u16(0), u16(0x21),
      u32(crc), u32(f.data.length), u32(f.data.length), u16(name.length),
      u16(0), u16(0), u16(0), u16(0), u32(0), u32(offset),
    );
    central.push(Uint8Array.from(cen), name);
    offset += local.length + name.length + f.data.length;
  });
  var cenSize = central.reduce(function (a, b) { return a + b.length; }, 0);
  var end = Uint8Array.from([].concat(
    u32(0x06054b50), u16(0), u16(0), u16(files.length), u16(files.length),
    u32(cenSize), u32(offset), u16(0),
  ));
  var parts = out.concat(central, [end]);
  var total = parts.reduce(function (a, b) { return a + b.length; }, 0);
  var buf = new Uint8Array(total);
  var pos = 0;
  parts.forEach(function (p) { buf.set(p, pos); pos += p.length; });
  return buf;
}

/** Build the template workbook (header row only) as a Uint8Array. */
function buildTemplateXlsx(headers) {
  var cols = headers || TEMPLATE_HEADERS;
  var X = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
  var sst =
    X + '<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="' + cols.length +
    '" uniqueCount="' + cols.length + '">' +
    cols.map(function (h) { return '<si><t>' + escapeXml(h) + '</t></si>'; }).join('') + '</sst>';
  var row =
    '<row r="1">' +
    cols.map(function (h, i) { return '<c r="' + colLetter(i) + '1" t="s"><v>' + i + '</v></c>'; }).join('') +
    '</row>';
  var sheet =
    X + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>' + row +
    '</sheetData></worksheet>';
  var files = [
    { name: '[Content_Types].xml', text: X + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/></Types>' },
    { name: '_rels/.rels', text: X + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>' },
    { name: 'xl/workbook.xml', text: X + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Employees" sheetId="1" r:id="rId1"/></sheets></workbook>' },
    { name: 'xl/_rels/workbook.xml.rels', text: X + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/></Relationships>' },
    { name: 'xl/worksheets/sheet1.xml', text: sheet },
    { name: 'xl/sharedStrings.xml', text: sst },
  ].map(function (f) { return { name: f.name, data: utf8(f.text) }; });
  return zipStore(files);
}

module.exports = {
  TEMPLATE_HEADERS: TEMPLATE_HEADERS,
  buildTemplateXlsx: buildTemplateXlsx,
  crc32: crc32,
};
